import { useState } from "react";
import { useDemo } from "../context/DemoContext";
import type { Bookmaker, Quote, MarketKey } from "@tracker-bet-analystic/domain";
import { parseDecimal, marketKeyToString, marketKeyFromString } from "@tracker-bet-analystic/domain";

interface QuoteFormProps {
  fixtureId: string;
  bookmakers: Bookmaker[];
}

const MARKET_OPTIONS: { key: MarketKey; label: string }[] = [
  { key: { family: "result_1x2", period: "FT90", subjectId: "match", selection: "home", settlementRule: "no_extra_time" }, label: "Resultado: Mandante vence (1)" },
  { key: { family: "result_1x2", period: "FT90", subjectId: "match", selection: "draw", settlementRule: "no_extra_time" }, label: "Resultado: Empate (X)" },
  { key: { family: "result_1x2", period: "FT90", subjectId: "match", selection: "away", settlementRule: "no_extra_time" }, label: "Resultado: Visitante vence (2)" },
  { key: { family: "both_teams_to_score", period: "FT90", subjectId: "match", selection: "yes", settlementRule: "no_extra_time" }, label: "Ambas Marcam: Sim" },
  { key: { family: "both_teams_to_score", period: "FT90", subjectId: "match", selection: "no", settlementRule: "no_extra_time" }, label: "Ambas Marcam: Não" },
  ...[0.5, 1.5, 2.5, 3.5, 4.5, 5.5].map((line) => ({
    key: { family: "total_goals" as const, period: "FT90" as const, subjectId: "match", selection: "over" as const, line, settlementRule: "no_extra_time" as const },
    label: `Total Gols: Over ${line}`,
  })),
  ...[0.5, 1.5, 2.5, 3.5, 4.5, 5.5].map((line) => ({
    key: { family: "total_goals" as const, period: "FT90" as const, subjectId: "match", selection: "under" as const, line, settlementRule: "no_extra_time" as const },
    label: `Total Gols: Under ${line}`,
  })),
  ...[-3, -2.5, -2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2, 2.5, 3].map((line) => ({
    key: { family: "handicap" as const, period: "FT90" as const, subjectId: "match", selection: "home" as const, line, settlementRule: "no_extra_time" as const },
    label: `Handicap Mandante: ${line > 0 ? `+${line}` : line}`,
  })),
];

export function QuoteForm({ fixtureId, bookmakers }: QuoteFormProps) {
  const { addQuote } = useDemo();
  const [bookmakerId, setBookmakerId] = useState("");
  const [marketKeyStr, setMarketKeyStr] = useState("");
  const [oddsInput, setOddsInput] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!bookmakerId) {
      setError("Selecione uma casa");
      return;
    }
    if (!marketKeyStr) {
      setError("Selecione um mercado");
      return;
    }
    const decimalOdds = parseDecimal(oddsInput);
    if (!decimalOdds) {
      setError("Odd inválida (deve ser > 1, ex: 1.91 ou 1,91)");
      return;
    }

    const marketKey = marketKeyFromString(marketKeyStr);
    if (!marketKey) {
      setError("Mercado inválido");
      return;
    }

    const bookmaker = bookmakers.find((b) => b.id === bookmakerId);
    if (!bookmaker) {
      setError("Casa não encontrada");
      return;
    }

    const now = new Date();
    const expiresAt = new Date(now.getTime() + 15 * 60 * 1000);

    const quote: Quote = {
      fixtureId,
      bookmakerId: bookmaker.id,
      bookmakerName: bookmaker.name,
      marketKey,
      decimalOdds,
      observedAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
    };

    addQuote(quote);
    setOddsInput("");
    setMarketKeyStr("");
    setBookmakerId("");
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 sm:items-end" aria-label="Registrar odd">
      <div className="sm:w-40">
        <label htmlFor="quote-bookmaker" className="label">Casa</label>
        <select
          id="quote-bookmaker"
          className="input"
          value={bookmakerId}
          onChange={(e) => setBookmakerId(e.target.value)}
          required
        >
          <option value="">Selecione</option>
          {bookmakers.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>

      <div className="sm:w-56">
        <label htmlFor="quote-market" className="label">Mercado</label>
        <select
          id="quote-market"
          className="input"
          value={marketKeyStr}
          onChange={(e) => setMarketKeyStr(e.target.value)}
          required
        >
          <option value="">Selecione</option>
          {MARKET_OPTIONS.map((opt) => (
            <option key={marketKeyToString(opt.key)} value={marketKeyToString(opt.key)}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="sm:w-24">
        <label htmlFor="quote-odds" className="label">Odd</label>
        <input
          id="quote-odds"
          type="text"
          className="input"
          placeholder="1.91"
          value={oddsInput}
          onChange={(e) => setOddsInput(e.target.value)}
          required
          inputMode="decimal"
        />
      </div>

      <button type="submit" className="btn btn-primary sm:w-28" disabled={bookmakers.length === 0}>
        Adicionar
      </button>

      {error && <span className="text-sm text-[var(--color-danger)]" role="alert">{error}</span>}
    </form>
  );
}